import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Star, Clock, Compass } from 'lucide-react'
import { api, type LibraryVideo, type LibraryTagCount } from '@/lib/api'
import { Card } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { StarButton } from '@/components/star-button'

const MIN_FAVORITES_FOR_TAG_RAIL = 8

function formatDuration(seconds: number | null): string {
  if (!seconds) return ''
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

function LibraryCard({
  video,
  onUnstar,
}: {
  video: LibraryVideo
  onUnstar: (id: string) => void
}) {
  const navigate = useNavigate()
  return (
    <Card
      className="group cursor-pointer overflow-hidden transition-all hover:border-primary/20 hover:shadow-md"
      onClick={() => navigate(`/videos/${video.id}`)}
    >
      <div className="flex gap-4 p-4">
        <div className="relative shrink-0">
          <img
            src={video.thumbnail_url || `https://img.youtube.com/vi/${video.youtube_video_id}/mqdefault.jpg`}
            alt=""
            className="h-24 w-40 rounded-lg bg-muted object-cover"
          />
          {video.duration_seconds && (
            <span className="absolute bottom-1 right-1 rounded bg-black/75 px-1.5 py-0.5 text-xs font-medium text-white">
              {formatDuration(video.duration_seconds)}
            </span>
          )}
          {/* Star sits top-right of thumbnail; clicking it removes the video
              from Library (parent uses onUnstar to drop the row from view). */}
          <div className="absolute right-1 top-1">
            <StarButton
              videoId={video.id}
              initialFavorited={video.is_favorited ?? true}
              variant="overlay"
              onChange={(fav) => { if (!fav) onUnstar(video.id) }}
            />
          </div>
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="line-clamp-2 font-medium leading-snug group-hover:text-primary">
            {video.title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">{video.channel_name}</p>

          {video.summary_short && (
            <p className="mt-2 line-clamp-2 text-sm text-foreground/80">
              {video.summary_short}
            </p>
          )}

          <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDistanceToNow(new Date(video.published_at), { addSuffix: true })}
            </span>
          </div>

          {video.tags && video.tags.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {video.tags.slice(0, 4).map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center rounded-full border bg-muted/40 px-2 py-0.5 text-[10px] font-medium text-foreground/70"
                >
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </Card>
  )
}

function TagRail({
  tags,
  selected,
  onSelect,
}: {
  tags: LibraryTagCount[]
  selected: string | null
  onSelect: (tag: string | null) => void
}) {
  return (
    <div className="mb-6 flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
          selected === null
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-input text-muted-foreground hover:text-foreground'
        }`}
      >
        All
      </button>
      {tags.map((t) => (
        <button
          key={t.tag}
          type="button"
          onClick={() => onSelect(t.tag)}
          className={`rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
            selected === t.tag
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-input text-muted-foreground hover:text-foreground'
          }`}
        >
          {t.tag}
          <span className={`ml-1.5 ${selected === t.tag ? 'text-primary-foreground/70' : 'text-muted-foreground/70'}`}>
            {t.count}
          </span>
        </button>
      ))}
    </div>
  )
}

function EmptyState() {
  return (
    <Card className="p-12 text-center">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-500/10">
        <Star className="h-6 w-6 text-amber-500" />
      </div>
      <h3 className="mt-4 font-semibold">Your Library is empty</h3>
      <p className="mx-auto mt-1 max-w-sm text-sm text-muted-foreground">
        Star videos to save them here. They'll stay in your Library even if you
        unfollow the channel. The perfect spot for the ideas you want to
        revisit.
      </p>
      <div className="mt-4">
        <Link to="/feed">
          <Button>
            <Compass className="mr-2 h-4 w-4" />
            Browse your feed
          </Button>
        </Link>
      </div>
    </Card>
  )
}

function NoMatchesState({ tag, onClear }: { tag: string; onClear: () => void }) {
  return (
    <Card className="p-10 text-center">
      <p className="text-sm text-muted-foreground">
        No saved videos tagged{' '}
        <span className="font-medium text-foreground">"{tag}"</span>.
      </p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onClear}>
        Clear filter
      </Button>
    </Card>
  )
}

function LibrarySkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i} className="p-4">
          <div className="flex gap-4">
            <Skeleton className="h-24 w-40 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-3/4" />
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-4 w-full" />
            </div>
          </div>
        </Card>
      ))}
    </div>
  )
}

export function LibraryPage() {
  const [videos, setVideos] = useState<LibraryVideo[]>([])
  const [tagCounts, setTagCounts] = useState<LibraryTagCount[]>([])
  const [total, setTotal] = useState(0)
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .getLibrary({ tag: selectedTag || undefined })
      .then((res) => {
        if (cancelled) return
        setVideos(res.videos)
        setTagCounts(res.tagCounts)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!cancelled) console.error('[library]', err)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [selectedTag])

  // When the user un-stars from inside Library, drop the row from view
  // immediately rather than waiting for a refetch. Total decrements so
  // the page header stays accurate.
  const onUnstar = (videoId: string) => {
    setVideos((prev) => prev.filter((v) => v.id !== videoId))
    setTotal((t) => Math.max(0, t - 1))
  }

  // Tag rail only renders once the user has enough saved videos that
  // grouping starts to feel meaningful. Below the threshold (and especially
  // when no tag has count >=2), the rail would be either empty or a wall
  // of count-1 chips — both worse than just showing a flat list.
  const showTagRail =
    total >= MIN_FAVORITES_FOR_TAG_RAIL && tagCounts.length > 0

  return (
    <div>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold">
            <Star className="h-5 w-5 fill-amber-500 text-amber-500" />
            Library
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {selectedTag
              ? `${videos.length} saved · tagged "${selectedTag}"`
              : total === 0
                ? 'Saved videos appear here'
                : `${total} saved video${total === 1 ? '' : 's'}`}
          </p>
        </div>
      </div>

      {showTagRail && (
        <TagRail tags={tagCounts} selected={selectedTag} onSelect={setSelectedTag} />
      )}

      {loading ? (
        <LibrarySkeleton />
      ) : videos.length === 0 ? (
        selectedTag ? (
          <NoMatchesState tag={selectedTag} onClear={() => setSelectedTag(null)} />
        ) : (
          <EmptyState />
        )
      ) : (
        <div className="space-y-3">
          {videos.map((v) => (
            <LibraryCard key={v.id} video={v} onUnstar={onUnstar} />
          ))}
        </div>
      )}
    </div>
  )
}
